/**
 * Spec 084 (gap 084-1) — Rule Library browser.
 *
 * Lists head-version rules (`is_current_version=true` by default) with
 * filters for status, epistemic_status, and scoring_mode. Each row links
 * to the detail page and — depending on status — offers an Activate or
 * Deprecate action. The detail route is `/admin/rules/:id` where `:id` is
 * always a UUID (never a slug), so the literal paths `/admin/rules/candidates`
 * and `/admin/rules/suggestions` never accidentally match `:id` (react-router
 * picks literal segments over params at the same depth regardless).
 *
 * Activate and Deprecate POST to the admin endpoints gated by
 * `rule_admin`/`orbit:admin` server-side.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon } from '../../components/ui/Icon.js';
import {
  activateRule,
  deprecateRule,
  listRules,
  type EpistemicStatus,
  type Rule,
  type RuleStatus,
  type ScoringMode,
} from '../../api/rules.js';
import { useToast } from '../../hooks/useToast.js';
import { DeprecateModal } from './DeprecateModal.js';
import { BackfillEmbeddingsBanner } from '../../components/BackfillEmbeddingsBanner.js';
import { RuleHealthPanel } from '../../components/RuleHealthPanel.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { SeedBadge } from '../../components/SeedBadge.js';
import { Select } from '../../components/Select.js';
import { SkeletonRows } from '../../components/Skeleton.js';
import { Button } from '../../components/ui/Button.js';
import { useFullBleed } from '../../lib/layout-width.js';

type RuleTab = 'rules' | 'health';

type StatusFilter = RuleStatus | 'all';
type EpistemicFilter = EpistemicStatus | 'all';
type ScoringFilter = ScoringMode | 'all';

const EPISTEMIC_OPTIONS: { value: EpistemicFilter; label: string }[] = [
  { value: 'all', label: 'any' },
  { value: 'community_heuristic_single_source', label: 'community_heuristic_single_source' },
  { value: 'community_consensus', label: 'community_consensus' },
  { value: 'research_backed', label: 'research_backed' },
  { value: 'internally_validated', label: 'internally_validated' },
];

export function RuleLibraryPage() {
  useFullBleed();
  const toast = useToast();
  const [rows, setRows] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [epistemic, setEpistemic] = useState<EpistemicFilter>('all');
  const [scoring, setScoring] = useState<ScoringFilter>('all');

  const [deprecating, setDeprecating] = useState<Rule | null>(null);
  const [actionPendingId, setActionPendingId] = useState<string | null>(null);

  // Spec 104 — two-tab layout: Rules (existing table) | Health (RuleHealthPanel).
  const [tab, setTab] = useState<RuleTab>('rules');

  // Phase 9 Session E.3 (gap 084-R2-1) — when arriving from the candidate
  // approve flow with `?slug=…` (or `?id=…`), highlight the matching row and
  // scroll it into view. The URL param is stripped after the highlight is
  // initiated so a manual reload doesn't re-trigger it.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightSlug = searchParams.get('slug');
  const highlightId = searchParams.get('id');
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listRules({
      status: status === 'all' ? undefined : status,
      epistemic_status: epistemic === 'all' ? undefined : epistemic,
      scoring_mode: scoring === 'all' ? undefined : scoring,
      is_current_version: true,
    })
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, epistemic, scoring, reloadToken]);

  // Phase 9 Session E.3 — when arriving from approve-redirect with a slug/id
  // hint, ensure the status filter is wide-open so the (typically
  // `approved_inactive`) new rule is included in the listing.
  useEffect(() => {
    if ((highlightSlug || highlightId) && status !== 'all') {
      setStatus('all');
    }
    // We deliberately only react to the URL-param presence on mount; if the
    // user later changes the filter manually, that wins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightSlug, highlightId]);

  // Highlight + scroll the matching row once rows have loaded.
  useEffect(() => {
    if (loading) return;
    if (!highlightSlug && !highlightId) return;
    if (rows.length === 0) return;
    const match =
      (highlightSlug && rows.find((r) => r.slug === highlightSlug)) ||
      (highlightId && rows.find((r) => r.id === highlightId)) ||
      null;
    if (!match) return;

    const key = match.id;
    setHighlightedKey(key);

    // Strip the search param so a reload doesn't re-trigger the highlight.
    const next = new URLSearchParams(searchParams);
    next.delete('slug');
    next.delete('id');
    setSearchParams(next, { replace: true });

    // Scroll the row into view on the next paint, then fade after 3s.
    const scrollTimer = window.setTimeout(() => {
      rowRefs.current.get(key)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 50);
    const fadeTimer = window.setTimeout(() => {
      setHighlightedKey(null);
    }, 3000);
    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(fadeTimer);
    };
    // We only want this effect to run when rows finish loading or the URL
    // hint changes — `searchParams`/`setSearchParams` are stable enough not
    // to thrash, but exclude them to avoid loops after we strip the param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows, highlightSlug, highlightId]);

  async function onActivate(rule: Rule) {
    setActionPendingId(rule.id);
    try {
      await activateRule(rule.id);
      toast.addToast('Rule activated', 'success');
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : 'Activate failed', 'error');
    } finally {
      setActionPendingId(null);
    }
  }

  async function onDeprecateConfirm(reason: string) {
    if (!deprecating) return;
    setActionPendingId(deprecating.id);
    try {
      await deprecateRule(deprecating.id, reason);
      toast.addToast('Rule deprecated', 'success');
      setDeprecating(null);
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(
        e instanceof Error ? e.message : 'Deprecate failed',
        'error',
      );
    } finally {
      setActionPendingId(null);
    }
  }

  return (
    <div data-testid="admin-rule-library" className="hub-page--table" data-density="compact">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Rule library' },
        ]}
      />
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title flex items-center gap-2">
            <Icon name="bx-book-open" />
            Rule library
          </h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
            Current-version rules available to the evaluation pipeline. Activate
            an approved rule to add it to the active set, or deprecate one to
            remove it.
          </p>
        </div>
      </div>

      {/* Spec 104 — tab strip: Rules | Health. */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <button
          type="button"
          data-testid="rule-tab-rules"
          onClick={() => setTab('rules')}
          className={
            'px-4 py-2 text-sm font-medium -mb-px border-b-2 ' +
            (tab === 'rules'
              ? 'border-violet-600 text-violet-700'
              : 'border-transparent text-slate-500 hover:text-slate-700')
          }
        >
          Rules
        </button>
        <button
          type="button"
          data-testid="rule-tab-health"
          onClick={() => setTab('health')}
          className={
            'px-4 py-2 text-sm font-medium -mb-px border-b-2 ' +
            (tab === 'health'
              ? 'border-violet-600 text-violet-700'
              : 'border-transparent text-slate-500 hover:text-slate-700')
          }
        >
          Health
        </button>
      </div>

      {tab === 'health' && <RuleHealthPanel />}

      {tab === 'rules' && (
        <>
      {/* Spec 100 — admin-gated banner that surfaces missing rule embeddings.
          Returns null for non-admins (DOM-absent) AND when count = 0, so the
          steady-state layout is unchanged from the pre-100 view. */}
      <BackfillEmbeddingsBanner />

      <div className="hub-toolbar">
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Status</span>
          <Select
            data-testid="rules-filter-status"
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'all', label: 'all' },
              { value: 'approved_inactive', label: 'approved_inactive' },
              { value: 'active', label: 'active' },
              { value: 'deprecated', label: 'deprecated' },
            ]}
          />
        </label>
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Epistemic status</span>
          <Select
            data-testid="rules-filter-epistemic"
            value={epistemic}
            onChange={(v) => setEpistemic(v as EpistemicFilter)}
            options={EPISTEMIC_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </label>
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Scoring mode</span>
          <Select
            data-testid="rules-filter-scoring"
            value={scoring}
            onChange={(v) => setScoring(v as ScoringFilter)}
            options={[
              { value: 'all', label: 'any' },
              { value: 'g_eval', label: 'g_eval' },
              { value: 'dag', label: 'dag' },
            ]}
          />
        </label>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="hub-table" data-testid="rules-table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Name</th>
              <th>Status</th>
              <th>Epistemic</th>
              <th className="is-num">Weight</th>
              <th>Version</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8}>
                  <SkeletonRows rows={7} cols={6} />
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && !err && (
              <tr>
                <td colSpan={8} className="text-center text-[var(--hub-color-ink-muted)]">
                  No rules match the current filters.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((rule) => {
                const isHighlighted = highlightedKey === rule.id;
                return (
                <tr
                  key={rule.id}
                  ref={(el) => {
                    if (el) {
                      rowRefs.current.set(rule.id, el);
                    } else {
                      rowRefs.current.delete(rule.id);
                    }
                  }}
                  data-testid={`rule-row-${rule.slug}`}
                  data-rule-id={rule.id}
                  data-rule-slug={rule.slug}
                  data-highlighted={isHighlighted ? 'true' : undefined}
                  className={
                    isHighlighted
                      ? 'transition-[background-color,box-shadow] duration-500 ring-2 ring-inset ring-[var(--hub-color-action)] bg-[var(--hub-color-action-soft)]'
                      : undefined
                  }
                >
                  <td className="font-mono text-xs">
                    {rule.slug}
                  </td>
                  <td>
                    <span className="inline-flex items-center gap-2">
                      {rule.name}
                      <SeedBadge createdBy={rule.created_by} />
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={rule.status} />
                  </td>
                  <td>
                    <EpistemicBadge status={rule.epistemic_status} />
                  </td>
                  <td className="is-num">
                    {rule.weight.toFixed(2)}
                  </td>
                  <td>v{rule.version}</td>
                  <td className="text-xs text-[var(--hub-color-ink-muted)]">
                    {new Date(rule.updated_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-3">
                      {rule.status === 'approved_inactive' && (
                        <Button
                          variant="ghost"
                          data-testid={`rule-activate-${rule.id}`}
                          onClick={() => onActivate(rule)}
                          disabled={actionPendingId === rule.id}
                        >
                          {actionPendingId === rule.id ? 'Working…' : 'Activate'}
                        </Button>
                      )}
                      {rule.status === 'active' && (
                        <Button
                          variant="danger"
                          data-testid={`rule-deprecate-${rule.id}`}
                          onClick={() => setDeprecating(rule)}
                          disabled={actionPendingId === rule.id}
                        >
                          Deprecate
                        </Button>
                      )}
                      {rule.status === 'deprecated' && (
                        <span className="text-xs font-medium text-[var(--hub-color-ink-subtle)]">
                          Archived
                        </span>
                      )}
                      <Link
                        to={`/admin/rules/${rule.id}`}
                        data-testid={`rule-details-${rule.id}`}
                        className="text-xs font-medium text-[var(--hub-color-action-text)]"
                      >
                        Details →
                      </Link>
                    </div>
                  </td>
                </tr>
                );
              })}
          </tbody>
        </table>
      </div>
        </>
      )}

      {deprecating && (
        <DeprecateModal
          ruleName={deprecating.name}
          pending={actionPendingId === deprecating.id}
          onCancel={() => setDeprecating(null)}
          onConfirm={onDeprecateConfirm}
        />
      )}
    </div>
  );
}

export function StatusBadge({ status }: { status: RuleStatus }) {
  const styles: Record<RuleStatus, string> = {
    active: 'bg-emerald-100 text-emerald-800',
    approved_inactive: 'bg-amber-100 text-amber-800',
    deprecated: 'bg-slate-200 text-slate-600',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {status}
    </span>
  );
}

export function EpistemicBadge({ status }: { status: EpistemicStatus }) {
  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
      {status}
    </span>
  );
}
